import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@components/Autocomplete';
import { Typography } from '@components/Typography';
import { SectionTitle, Hint } from '@features/ChannelsForms/FormSection';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Switch } from '@components/Switch';
import TeamSelect from '@features/TeamSelect';
import BrandManageModal from '@features/BrandManageModal';
import { useContactFieldsValues } from '@features/ContactFieldsSettings';
import { useTranslation, useChannels, useLocations, usePermissions } from '@hooks';
import { CHANNEL, STATUS } from '@constants/channels';
import { FORM_TYPE } from '@constants/brands';
import { LOCATIONS_MODE, PLUSPOINT_EMAIL } from '@constants/liveChat';
import { getChannelLinkScope, isChannelInLinkScope } from '@helpers/channels';
import { brandsActions, labelsActions } from '@actions';
import { isFollowUpHiddenFromEditor, requiredContactFields } from './payload';
import CollectInformationBlock from './CollectInformation';

const Container = styled('div')({
    display: 'grid',
    gridGap: '8px',
    gridTemplateColumns: '100%',
});

const LocationsModeGroup = styled(RadioGroup)({
    flexDirection: 'row',
    gap: 16,
    '& .MuiFormControlLabel-root': {
        marginLeft: '-3px',
        marginRight: 0,
    },
});

const radioSx = { color: 'var(--text-secondary)', padding: '6px', '&.Mui-checked': { color: 'var(--primary-main)' } };

const isPhoneChannel = channel => channel.type === CHANNEL.SMS || channel.type === CHANNEL.WHATSAPP;

const LiveChatForm = ({ selectedChannel }) => {
    const { t } = useTranslation();

    const { data: labels } = useSelector(state => state.labels);
    const brands = useSelector(state => state.brands.data);
    const locations = useSelector(state => state.account?.account?.companies || []);

    const dispatch = useDispatch();
    const { channels, getAccessibleChannelIds, getChannelOptions } = useChannels();
    const { getLocationsOptionsByBrand } = useLocations();
    const { hasFullSettings } = usePermissions();
    const { control, setValue, getValues, watch } = useFormContext();
    const [followUpNotice, setFollowUpNotice] = useState(null);
    const [brandModal, setBrandModal] = useState({ open: false, type: FORM_TYPE.NEW });

    const brandId = watch('brandId');
    const assignLocation = watch('assignLocation');
    const locationsMode = watch('locationsMode');
    const notifyAboutMissingConversations = watch('notifyAboutMissingConversations');
    const followUpChannelId = watch('followUpChannelId');
    const { phoneRequired, emailRequired } = requiredContactFields(useContactFieldsValues(['email', 'phone']));
    const anyContactRequired = phoneRequired || emailRequired;

    useEffect(() => {
        dispatch(labelsActions.get());
    }, []);

    useEffect(() => {
        if (brands.length === 1 && !getValues('brandId')) {
            setValue('brandId', brands[0].id, { shouldValidate: true });
        }
    }, [brands]);

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);
    const brandLocationsOptions = useMemo(() => getLocationsOptionsByBrand(brandId), [brandId, locations]);

    const linkScope = useMemo(() => getChannelLinkScope({ locationId: '', brandId }, locations), [brandId, locations]);
    const storedFollowUpId =
        selectedChannel?.brandId && selectedChannel.brandId === brandId
            ? selectedChannel.liveChatParameters?.followUpChannelId
            : null;

    const isFollowUpCandidate = useCallback(
        channel =>
            ((phoneRequired && isPhoneChannel(channel)) || (emailRequired && channel.type === CHANNEL.EMAIL)) &&
            (isChannelInLinkScope(channel, linkScope) || channel.id === storedFollowUpId),
        [phoneRequired, emailRequired, linkScope, storedFollowUpId],
    );

    const followUpHiddenFromEditor = isFollowUpHiddenFromEditor(selectedChannel, storedFollowUpId, channels);

    const followUpOptions = useMemo(() => {
        const options = getChannelOptions(
            { out: true, withPluspointEmail: emailRequired },
            getAccessibleChannelIds(isFollowUpCandidate),
        );
        return followUpHiddenFromEditor
            ? [...options, { value: storedFollowUpId, label: t('SettingsChannels.followUpChannelOtherTeam') }]
            : options;
    }, [channels, emailRequired, isFollowUpCandidate, followUpHiddenFromEditor, storedFollowUpId]);

    useEffect(() => {
        if (!notifyAboutMissingConversations || !followUpChannelId) return;
        if (followUpOptions.some(option => option.value === followUpChannelId)) return;
        const stored = channels.find(channel => channel.id === followUpChannelId);
        const stillUsable = followUpChannelId === PLUSPOINT_EMAIL || stored?.status === STATUS.SUCCESS;
        setValue('followUpChannelId', '', { shouldValidate: true });
        setFollowUpNotice(stillUsable ? 'followUpChannelCleared' : 'followUpChannelUnavailable');
    }, [notifyAboutMissingConversations, followUpChannelId, followUpOptions, channels]);

    useEffect(() => {
        if (anyContactRequired || !notifyAboutMissingConversations) return;
        setValue('notifyAboutMissingConversations', false, { shouldValidate: true });
        setValue('followUpChannelId', '', { shouldValidate: true });
        setFollowUpNotice(null);
    }, [anyContactRequired, notifyAboutMissingConversations]);

    useEffect(() => {
        if (notifyAboutMissingConversations && emailRequired && !followUpChannelId && !followUpNotice) {
            setValue('followUpChannelId', PLUSPOINT_EMAIL, { shouldValidate: true });
        }
    }, [notifyAboutMissingConversations, emailRequired, followUpChannelId, followUpNotice]);

    return (
        <Container>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <TeamSelect data={selectedChannel} />
            {brands.length !== 1 ? (
                <Controller
                    control={control}
                    name="brandId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                if (value === 'create-new') return;
                                setValue('brandId', value, { shouldValidate: true, shouldDirty: true });
                                setValue('locations', [], { shouldValidate: true, shouldDirty: true });
                            }}
                            label={t('common.brand')}
                            options={brandsOptions}
                            fullWidth
                            required
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            creatable={hasFullSettings()}
                            creatableText={t('SettingsBrands.createNewBrand')}
                            creatableClick={() => setBrandModal({ open: true, type: FORM_TYPE.NEW })}
                        />
                    )}
                />
            ) : null}
            {brandModal.open ? (
                <BrandManageModal
                    modal={brandModal}
                    onClose={() => setBrandModal({ open: false, type: FORM_TYPE.NEW })}
                    refetchCallback={() => dispatch(brandsActions.getAll())}
                />
            ) : null}
            <SectionTitle>{t('FeedbackSurveys.contactDetails')}</SectionTitle>
            <Hint>{t('SettingsChannels.contactDetailsHint')}</Hint>
            <CollectInformationBlock labels={labels} />
            <SectionTitle sx={{ marginTop: '8px' }}>{t('common.location')}</SectionTitle>
            <Controller
                control={control}
                name="assignLocation"
                render={({ field }) => (
                    <Switch
                        checked={!!field.value}
                        handleChange={event =>
                            setValue('assignLocation', event.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                            })
                        }
                        label={t('SettingsChannels.assignLocationSwitch')}
                    />
                )}
            />
            <Hint sx={{ marginTop: '-4px' }}>{t('SettingsChannels.assignLocationHint')}</Hint>
            {assignLocation ? (
                <>
                    <Controller
                        control={control}
                        name="locationsMode"
                        render={({ field }) => (
                            <LocationsModeGroup
                                value={field.value}
                                onChange={event =>
                                    setValue('locationsMode', event.target.value, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    })
                                }
                            >
                                <FormControlLabel
                                    value={LOCATIONS_MODE.ALL}
                                    control={<Radio sx={radioSx} />}
                                    label={
                                        <Typography variant="body2">
                                            {t('SettingsChannels.allLocationsCount', {
                                                number: brandLocationsOptions.length,
                                            })}
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    value={LOCATIONS_MODE.SELECTED}
                                    control={<Radio sx={radioSx} />}
                                    label={
                                        <Typography variant="body2">
                                            {t('SettingsChannels.selectedLocations')}
                                        </Typography>
                                    }
                                />
                            </LocationsModeGroup>
                        )}
                    />
                    {locationsMode === LOCATIONS_MODE.SELECTED ? (
                        <Controller
                            control={control}
                            name="locations"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Autocomplete
                                    {...field}
                                    ref={ref}
                                    label={t('common.locations')}
                                    options={brandLocationsOptions}
                                    value={field.value}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    inputProps={{ style: { display: field?.value?.length > 0 ? 'none' : 'block' } }}
                                    onChangeAutoComplete={items => {
                                        setValue('locations', items.length ? items : [], {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                    groupMultiProps={{
                                        getOptionLabel: option => option.label,
                                    }}
                                    required
                                    error={error}
                                    className="locations-autocomplete"
                                />
                            )}
                        />
                    ) : null}
                </>
            ) : null}
            <SectionTitle sx={{ marginTop: '8px' }}>{t('SettingsChannels.reachVisitorsTitle')}</SectionTitle>
            <Controller
                control={control}
                name="notifyAboutMissingConversations"
                render={({ field }) => (
                    <Switch
                        checked={!!field.value}
                        disabled={!anyContactRequired}
                        handleChange={event => {
                            setValue('notifyAboutMissingConversations', event.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                            if (!event.target.checked) {
                                setValue('followUpChannelId', '', { shouldValidate: true });
                                setFollowUpNotice(null);
                            }
                        }}
                        label={t('SettingsChannels.missedRepliesSwitch')}
                    />
                )}
            />
            <Hint sx={{ marginTop: '-4px' }}>
                {t(
                    anyContactRequired
                        ? 'SettingsChannels.missedRepliesHint'
                        : 'SettingsChannels.followUpRequiresContact',
                )}
            </Hint>
            {notifyAboutMissingConversations ? (
                <>
                    <Controller
                        control={control}
                        name="followUpChannelId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                ref={ref}
                                onChange={({ target: { value } }) => {
                                    setValue('followUpChannelId', value, { shouldValidate: true, shouldDirty: true });
                                    setFollowUpNotice(null);
                                }}
                                label={t('common.channel')}
                                options={followUpOptions}
                                fullWidth
                                required
                                error={!!error}
                                helperText={error && !followUpNotice ? t(error.message) : null}
                            />
                        )}
                    />
                    {followUpNotice ? (
                        <Hint sx={{ color: 'var(--error-main)', marginTop: '-12px' }}>
                            {t(`SettingsChannels.${followUpNotice}`)}
                        </Hint>
                    ) : null}
                </>
            ) : null}
        </Container>
    );
};

export default LiveChatForm;
